<template>
    <v-container fluid class="sensor-log-page">
        <div class="sensor-log-heading">
            <v-btn icon class="sensor-log-back" aria-label="Go back" @click="goBack">
                <v-icon class="text--white80" style="color: rgba(255, 255, 255, 0.8)">
                    mdi-arrow-u-left-top
                </v-icon>
            </v-btn>
            <h3 class="sensor-log-title text--white80">{{ sensorName }} LOG</h3>
            <span class="sensor-log-heading__balance" aria-hidden="true"></span>
        </div>

        <div class="sensor-log-scroll">
            <v-timeline
                dense
                clipped
                :class="{ 'transparent-line': logItems.length <= 1 }"
                class="sensor-log-timeline"
            >
                <v-timeline-item v-for="(item, index) in logItems" :key="index" color="white">
                    <template v-slot:icon>
                        <v-avatar size="40">
                            <img src="@/assets/icons/reliable-b-m-s.svg" v-if="item.latestEventName.indexOf('No event') !== -1" />
                            <img src="@/assets/icons/door-opened.svg" v-else-if="item.latestEventName.indexOf('Door opened') !== -1" />
                            <img src="@/assets/icons/door-closed.svg" v-else-if="item.latestEventName.indexOf('Door closed') !== -1" />
                            <img src="@/assets/icons/paired.svg" v-else-if="item.latestEventName.indexOf('Device paired successfully') !== -1" />
                            <img src="@/assets/icons/switched-on.svg" v-else-if="item.latestEventName.indexOf('Device ON') !== -1" />
                            <img src="@/assets/icons/switched-off.svg" v-else-if="item.latestEventName.indexOf('Device OFF') !== -1" />
                            <img src="@/assets/icons/bilge-pump-on.svg" v-else-if="item.latestEventName.indexOf('Bilge pump ON') !== -1" />
                            <img src="@/assets/icons/bilge-pump-off.svg" v-else-if="item.latestEventName.indexOf('Bilge pump OFF') !== -1" />
                            <img src="@/assets/icons/wired-disconnected.svg" v-else-if="item.latestEventName.indexOf('Sensor unplugged') !== -1" />
                            <img src="@/assets/icons/wired-connected.svg" v-else-if="item.latestEventName.indexOf('Sensor plugged in') !== -1" />
                            <img src="@/assets/icons/tamper-triggered.svg" v-else-if="item.latestEventName.indexOf('Tamper triggered') !== -1" />
                            <img src="@/assets/icons/siren-red.svg" v-else-if="item.latestEventName.startsWith('Alarm')" />
                            <img src="@/assets/icons/warning-red.svg" v-else-if="item.latestEventName.indexOf('Disconnected') !== -1" />
                            <img src="@/assets/icons/unresponsive.svg" v-else-if="item.latestEventName.indexOf('Unresponsive') !== -1" />
                            <img src="@/assets/icons/responsive.svg" v-else-if="item.latestEventName.indexOf('Responsive') !== -1" />
                            <img src="@/assets/icons/reconnecting.svg" v-else-if="item.latestEventName.indexOf('Reconnecting') !== -1" />
                            <img src="@/assets/icons/unknown-error.svg" v-else-if="item.latestEventName.indexOf('Unknown event') !== -1" />
                            <img src="@/assets/icons/unknown-error.svg" v-else />
                        </v-avatar>
                    </template>
                    <v-row class="sensor-event-row no-gutters" align="center">
                        <v-col class="sensor-event-name white--text">
                            {{ item.latestEventName }}
                        </v-col>
                        <v-col class="sensor-event-date text-right white--text">
                            <time :datetime="item.modifiedDate">
                                <span class="sensor-event-date__day">{{ formatModifiedDate(item.modifiedDate) }}</span>
                                <span class="sensor-event-date__time">{{ formatModifiedTime(item.modifiedDate) }}</span>
                            </time>
                        </v-col>
                    </v-row>
                </v-timeline-item>
            </v-timeline>
        </div>
    </v-container>
</template>
<script lang="ts">
    import {
        computed,
        ref,
        getCurrentInstance,
        defineComponent
    } from "vue";
    import useSensorHistory from "@/store/useSensorHistory";
    import useDevice from "@/utilities/useDevice";
    import moment from 'moment-timezone';
    import useUserSettings from "@/store/useUserSettings";
    import UserSettingsViewModel from "@/models/UserSettingsViewModel";

    export default defineComponent({
        setup() {
            const root = getCurrentInstance()?.proxy as any;
            const id = root.$route.params.id;
            const { getUserSettings } = useUserSettings();
            const userSettings = ref<UserSettingsViewModel>();
            getUserSettings().then((value: UserSettingsViewModel) => {
                userSettings.value = value;
            });
            const { logItems } = useSensorHistory(id);
            const timezone = computed(() => userSettings.value?.timezone || "UTC");
            const formatModifiedDate = (modifiedDate: string) => {
                return moment.utc(modifiedDate).tz(timezone.value).format("MMM DD, YYYY");
            };
            const formatModifiedTime = (modifiedDate: string) => {
                return moment.utc(modifiedDate).tz(timezone.value).format("HH:mm:ss");
            };
            const sensorId = parseInt(id);
            const sensorName = useDevice().getName(sensorId).toUpperCase();

            const goBack = () => {
                window.history.back();
            };

            return {
                logItems,
                sensorName,
                formatModifiedDate,
                formatModifiedTime,
                goBack
            };
        }
    });
</script>
<style lang="scss" scoped>
    .sensor-log-page {
        position: absolute;
        inset: 0;
        display: flex;
        width: 100%;
        max-width: 100%;
        min-height: 0;
        flex-direction: column;
        overflow: hidden;
        box-sizing: border-box;
        padding: 0 12px;
    }

    .sensor-log-heading {
        display: grid;
        flex: 0 0 auto;
        grid-template-columns: 40px minmax(0, 1fr) 40px;
        align-items: center;
        gap: 8px;
        padding: 10px 0 8px;
    }

    .sensor-log-back {
        width: 40px;
        height: 40px;
        margin: 0;
    }

    .sensor-log-back ::v-deep .v-icon {
        font-size: 24px !important;
    }

    .sensor-log-title {
        margin: 0;
        font-size: var(--app-font-emphasis);
        line-height: 1.2;
        text-align: center;
    }

    .sensor-log-scroll {
        flex: 1 1 auto;
        min-height: 0;
        padding: 0 12px 12px 0;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
    }

    .sensor-log-timeline {
        --event-divider-width: 64px;
        --event-leading-gap: 6px;
        margin: 0;
        padding-right: 0;
        overflow: visible;
    }

    .sensor-log-timeline ::v-deep .v-timeline-item__divider {
        min-width: var(--event-divider-width);
    }

    .sensor-log-timeline ::v-deep .v-timeline-item__body {
        max-width: calc(100% - var(--event-divider-width) - var(--event-leading-gap));
    }

    .sensor-event-row {
        flex-wrap: nowrap;
        gap: 8px;
    }

    .sensor-event-name,
    .sensor-event-date {
        padding: 3px 4px !important;
        line-height: 1.2;
    }

    .sensor-event-name {
        min-width: 0;
        flex: 1 1 auto;
        font-size: var(--app-font-body);
    }

    .sensor-event-date {
        display: flex;
        width: max-content;
        min-width: max-content;
        flex: 0 0 auto;
        flex-direction: column;
        align-items: flex-end;
        font-size: var(--app-font-caption);
    }

    .sensor-event-date time {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 1px;
    }

    .sensor-event-date__day {
        color: rgba(255, 255, 255, 0.7);
        font-size: var(--app-font-tiny);
        font-weight: 400;
    }

    .sensor-event-date__time {
        color: rgba(255, 255, 255, 0.95);
        font-size: var(--app-font-small);
        font-weight: 600;
    }

    .theme--light.v-timeline:before {
        background: white;
        top: 40px;
        height: auto;
        left: 37px !important;
        right: auto !important;
    }

    .theme--light.v-timeline-item {
        align-items: center;
    }

    .v-timeline-item:last-child {
        padding-bottom: 0;
    }

    ::v-deep .v-timeline-item {
        padding-bottom: 12px;
    }

    ::v-deep .v-timeline-item:last-child {
        padding-bottom: 0;
    }

    .transparent-line::v-deep.v-timeline:before {
        background: transparent !important;
    }
</style>